import {
  DragDropProvider,
  DropTarget,
} from '@wandb/weave/common/containers/DragDropContainer';
import {SetState} from '@wandb/weave/common/util/types';
import produce from 'immer';
import React, {useCallback} from 'react';

import {useDragDropReorder} from '../../util/dragDrop';
import {ShowcasedLink} from './context';
import * as S from './LinkCards.styles';

const ITEM_DROPZONE_PADDING_PX = 8;

type LinkCardsProps = {
  items: ShowcasedLink[];
  setItems: SetState<ShowcasedLink[]>;
  readOnly: boolean;
};

export const LinkCards: React.FC<LinkCardsProps> = React.memo(
  ({items, setItems, readOnly}) => {
    const {
      makeDragSourceCallbackRef,
      renderDropIndicators,
      onDragOver,
      onDragEnd,
      onDrop,
    } = useDragDropReorder({
      reorder: useCallback(
        (fromIndex: number, toIndex: number) => {
          setItems(prev =>
            produce(prev, draft => {
              const item = draft[fromIndex];
              draft.splice(fromIndex, 1);
              draft.splice(toIndex, 0, item);
            })
          );
        },
        [setItems]
      ),
      dropzonePadding: ITEM_DROPZONE_PADDING_PX,
    });

    return (
      <DragDropProvider>
        <DropTarget
          partRef={{id: `target`}}
          onDragOver={onDragOver}
          onDrop={onDrop}>
          <S.Grid>
            {items.map(({url, name}, i) => (
              <S.Card key={i} to={url} ref={makeDragSourceCallbackRef(i)}>
                <S.CardContent
                  partRef={{id: `${i}`}}
                  draggingStyle={{opacity: 0.25}}
                  onDragEnd={onDragEnd}>
                  {renderDropIndicators(i)}
                  <S.CardLinkIconContainer>
                    <S.CardLinkIcon />
                  </S.CardLinkIconContainer>
                  <S.CardText>
                    <S.CardHeader>{name}</S.CardHeader>
                    <S.CardURL>{url}</S.CardURL>
                  </S.CardText>
                  <S.CardActions>
                    {!readOnly && (
                      <>
                        <S.DeleteButton
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            setItems(prev =>
                              produce(prev, draft => {
                                draft.splice(i, 1);
                              })
                            );
                          }}>
                          <S.DeleteIconLight />
                          <S.DeleteIconGray />
                          <S.DeleteIconRed />
                        </S.DeleteButton>
                        <S.DragHandle partRef={{id: `${i}`}}>
                          <S.DragHandleIcon />
                        </S.DragHandle>
                      </>
                    )}
                  </S.CardActions>
                </S.CardContent>
              </S.Card>
            ))}
          </S.Grid>
        </DropTarget>
      </DragDropProvider>
    );
  }
);
